import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import GridItem from '../../../components/Grid/GridItem.js';
import GridContainer from '../../../components/Grid/GridContainer.js';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { Card, CardContent, CircularProgress, Box, Typography } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllRulesets,
  createRuleset,
  updateRuleset,
  deleteRuleset,
  getAllDiscords,
  Ruleset,
} from './RulesetApi';
import { RulesetForm } from './RulesetForm';
import RulesetsTable from './RulesetsTable';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { RequireAuth } from '../../../components/Auth/RequireAuth';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    '&&&&': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      margin: '0 auto',
      width: 'auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  headLineStyle: {
    fontWeight: '500',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
  contentContainer: {
    width: '100%',
    color: theme.palette.text.primary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  formSection: {
    marginTop: theme.spacing(4),
  },
}));

function RulesetsPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  useDocumentTitle(t('navigation.rulesets'));
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  const [editingRuleset, setEditingRuleset] = useState<Ruleset | undefined>(undefined);

  // Query for fetching all rulesets
  const {
    data: rulesets,
    isLoading: isLoadingRulesets,
    isError: isRulesetsError,
  } = useQuery({
    queryKey: ['rulesets'],
    queryFn: getAllRulesets,
  });

  // Query for fetching all Discord teams
  const {
    data: discords,
    isLoading: isLoadingDiscords,
    isError: isDiscordsError,
  } = useQuery({
    queryKey: ['discords'],
    queryFn: getAllDiscords,
  });

  // Mutation for creating a new ruleset
  const createRulesetMutation = useMutation({
    mutationFn: createRuleset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rulesets'] });
      toast.success(t('rulesets.messages.createSuccess'));
      setEditingRuleset(undefined);
    },
    onError: (error) => {
      console.error('Error creating ruleset:', error);
      toast.error(t('rulesets.messages.createError'));
    },
  });

  // Mutation for updating an existing ruleset
  const updateRulesetMutation = useMutation({
    mutationFn: (ruleset: Ruleset) => {
      if (!ruleset.id) throw new Error('Ruleset ID is required for update');
      return updateRuleset(ruleset.id, ruleset);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rulesets'] });
      toast.success(t('rulesets.messages.updateSuccess'));
      setEditingRuleset(undefined);
    },
    onError: (error) => {
      console.error('Error updating ruleset:', error);
      toast.error(t('rulesets.messages.updateError'));
    },
  });

  // Mutation for deleting a ruleset
  const deleteRulesetMutation = useMutation({
    mutationFn: deleteRuleset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rulesets'] });
      toast.success(t('rulesets.messages.deleteSuccess'));
    },
    onError: (error) => {
      console.error('Error deleting ruleset:', error);
      toast.error(t('rulesets.messages.deleteError'));
    },
  });

  const handleEdit = useCallback((ruleset: Ruleset) => {
    setEditingRuleset(ruleset);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('ruleset-form-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      deleteRulesetMutation.mutate(id);
    },
    [deleteRulesetMutation]
  );

  const handleFormSubmit = useCallback(
    (ruleset: Ruleset) => {
      if (editingRuleset && editingRuleset.id) {
        updateRulesetMutation.mutate({ ...ruleset, id: editingRuleset.id });
      } else {
        createRulesetMutation.mutate(ruleset);
      }
    },
    [editingRuleset, updateRulesetMutation, createRulesetMutation]
  );

  const handleFormCancel = useCallback(() => {
    setEditingRuleset(undefined);
  }, []);

  if (!sessionToken) {
    return <Navigate to="/login" replace />;
  }

  const isLoading = isLoadingRulesets || isLoadingDiscords;
  const isError = isRulesetsError || isDiscordsError;

  return (
    <RequireAuth requiredPermission="is_staff">
      <GridItem md={12} className={classes.mainContainer}>
        <div className={classes.contentContainer}>
          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Card>
              <CardContent>
                <Typography color="error">{t('common.errorLoadingData')}</Typography>
              </CardContent>
            </Card>
          ) : (
            <GridContainer justify="center">
              {/* Rulesets Table Section */}
              <GridItem xs={12} className={classes.section}>
                <RulesetsTable
                  rulesets={rulesets || []}
                  discords={discords || []}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </GridItem>

              {/* Ruleset Form Section */}
              <GridItem xs={12} id="ruleset-form-section" className={classes.formSection}>
                <RulesetForm
                  ruleset={editingRuleset}
                  discords={discords || []}
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormCancel}
                  isLoading={createRulesetMutation.isPending || updateRulesetMutation.isPending}
                />
              </GridItem>
            </GridContainer>
          )}
        </div>
      </GridItem>
    </RequireAuth>
  );
}

export { RulesetsPage };
